/**
 * The body of a treatment entry, shared by the standalone timeline card and the
 * treatments listed inside a consultation.
 */
import {
  Ban,
  Building2,
  Clock,
  EyeOff,
  Package,
  Pill,
  ScrollText,
  Stethoscope,
  Syringe,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'

import {
  formatClinicPrice,
  getAdministeredBatches,
  getClinic,
  getPrimaryOrder,
  type TreatmentPrescriptionContext,
  type TreatmentWithRelations,
} from '@snootsvet/shared'

import { ExpandBadge } from '@/frontend/components/expand-badge'
import { FormattedDateTime } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'
import { OrderStatusBadge } from '@/frontend/features/orders/order-badges'
import { RefillBadge } from '@/frontend/features/patient/refill-badges'
import { TreatmentAttributes } from '@/frontend/features/patient/timeline/treatment-attributes'
import { TreatmentOrders } from '@/frontend/features/patient/timeline/treatment-orders'
import { cn } from '@/frontend/lib/utils'

type Treatment = TreatmentWithRelations & TreatmentPrescriptionContext

/** The icon standing in for the treatment's type, which the flat timeline no longer groups by. */
function getTypeIcon(treatment: Treatment) {
  if (treatment.treatmentType === 'vaccination') return Syringe
  if (treatment.treatmentType === 'procedure') return Package
  return treatment.isExternal ? ScrollText : Pill
}

export function TreatmentRow({
  treatment,
  menu,
  showMeta = false,
  iconPosition = 'gutter',
  className,
}: {
  treatment: Treatment
  /** Rendered flush right on the heading row. */
  menu?: ReactNode
  /** Treatments recorded outside a consultation carry their own date, vet and clinic. */
  showMeta?: boolean
  /** Where the type icon sits: in a left gutter, or inline after the product name. */
  iconPosition?: 'gutter' | 'inline'
  /** Merged onto the body block that carries the heading, details and notes. */
  className?: string
}) {
  const [ordersOpen, setOrdersOpen] = useState(false)
  const Icon = getTypeIcon(treatment)
  const primaryOrder = getPrimaryOrder(treatment.orders)
  const primaryOrderClinic = primaryOrder ? getClinic(primaryOrder.clinicId) : null

  return (
    <div>
      <div className={cn('flex items-start gap-3 px-4 py-3', className)}>
        {iconPosition === 'gutter' && (
          <Icon className="size-3.5 shrink-0 self-center text-muted-foreground" />
        )}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="flex items-center gap-1.5">
                {iconPosition === 'inline' && (
                  <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                )}
                <span className="text-headline-sm">{treatment.productInfo.name}</span>
              </span>
              {treatment.isExternal && (
                <Badge variant="pending" size="xs">
                  External
                </Badge>
              )}
              {treatment.treatmentType === 'vaccination' && treatment.catalogProduct && (
                <Badge variant="outline" size="xs">
                  Historical
                </Badge>
              )}
              {showMeta && primaryOrderClinic && (
                <Badge variant="neutral" size="xs" className="font-normal">
                  <Building2 className="text-muted-foreground" />
                  {primaryOrderClinic.name}
                </Badge>
              )}
              {primaryOrder && (
                <OrderStatusBadge
                  status={primaryOrder.status}
                  size="xs"
                  title={primaryOrder.notes ?? undefined}
                />
              )}
              <RefillBadge rx={treatment} />
              {treatment.orders.length > 0 && (
                <ExpandBadge
                  open={ordersOpen}
                  onToggle={() => setOrdersOpen(!ordersOpen)}
                  icon={<Package />}
                >
                  Orders ({treatment.orders.length})
                </ExpandBadge>
              )}
            </div>
            {/* Clamped to the heading's own height so the taller icon button doesn't
                push the meta row down. */}
            <div className="flex h-5 shrink-0 items-center gap-2">
              {primaryOrder && primaryOrderClinic && (
                <span className="text-sm font-semibold tabular-nums">
                  {formatClinicPrice(primaryOrder.totalPrice, primaryOrder.clinicId)}
                </span>
              )}
              {menu && <div className="-mr-2 flex items-center">{menu}</div>}
            </div>
          </div>

          {showMeta && (
            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="size-3 shrink-0" />
                <FormattedDateTime
                  value={treatment.createdAt}
                  clinicId={primaryOrder?.clinicId ?? null}
                />
              </span>
              <span className="flex items-center gap-1">
                <Stethoscope className="size-3 shrink-0" />
                {treatment.staff.firstName} {treatment.staff.lastName}
              </span>
            </div>
          )}

          <TreatmentAttributes
            className="mt-1.5"
            dispensedQuantity={
              treatment.administeredInConsult ? null : treatment.productInfo.quantityLabel
            }
            administeredQuantity={
              treatment.administeredInConsult ? treatment.productInfo.quantityLabel : null
            }
            batchNumbers={
              // Stock is allocated per order, so a take-home treatment with refills can
              // draw from several lots over its life. Only a treatment administered on
              // the spot has a single draw for the row to name.
              treatment.administeredInConsult
                ? getAdministeredBatches(treatment).map((b) => b.batchNumber)
                : null
            }
            repeatsAllowed={treatment.repeatsAllowed}
            refillInterval={treatment.refillInterval}
            instructions={treatment.instructions}
            tagNumber={treatment.tagNumber}
            injectionSite={treatment.injectionSite}
          >
            {treatment.clinicalNotes && (
              <span title="Clinical notes" className="flex items-start gap-1">
                <Stethoscope className="mt-[0.2rem] size-3 shrink-0" />
                <span className="whitespace-pre-wrap">{treatment.clinicalNotes}</span>
              </span>
            )}
            {treatment.internalNotes && (
              <span title="Internal notes (staff only)" className="flex items-start gap-1">
                <EyeOff className="mt-[0.2rem] size-3 shrink-0" />
                <span className="whitespace-pre-wrap">{treatment.internalNotes}</span>
              </span>
            )}
            {treatment.cancelledReason && (
              <span
                title="Cancellation reason"
                className="flex items-start gap-1 text-destructive-fg"
              >
                <Ban className="mt-[0.2rem] size-3 shrink-0" />
                <span className="whitespace-pre-wrap">
                  <span className="font-medium">Cancelled:</span> {treatment.cancelledReason}
                </span>
              </span>
            )}
          </TreatmentAttributes>
        </div>
      </div>

      {treatment.orders.length > 0 && ordersOpen && (
        <div className="border-t px-4 py-2.5">
          <TreatmentOrders treatment={treatment} />
        </div>
      )}
    </div>
  )
}
